import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SID_KEY = "print-plug-chat-session";

export const ChatBubble = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SID_KEY) || "");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [nameError, setNameError] = useState("");
  const [closed, setClosed] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);

  const poll = async (sid) => {
    if (!sid) return;
    try {
      const { data } = await axios.get(`${API}/chat/${sid}/messages`);
      setMessages(data.messages);
      setClosed(Boolean(data.session.closed));
      if (!open) {
        setUnread((prev) => (data.session.visitor_unread > 0 ? data.session.visitor_unread : prev));
      }
    } catch {
      if (localStorage.getItem(SID_KEY) === sid) {
        localStorage.removeItem(SID_KEY);
        setSessionId("");
      }
    }
  };

  useEffect(() => {
    poll(sessionId);
    const t = setInterval(() => poll(sessionId), 4000);
    return () => clearInterval(t);
  }, [sessionId, open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const startChat = async () => {
    if (!name.trim()) {
      setNameError("Name needed to start");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setNameError("Email needed so we can notify you of replies");
      return;
    }
    setNameError("");
    const { data } = await axios.post(`${API}/chat/start`, { name: name.trim(), email: email.trim() });
    localStorage.setItem(SID_KEY, data.session_id);
    setSessionId(data.session_id);
  };

  const startNew = () => {
    localStorage.removeItem(SID_KEY);
    setSessionId("");
    setMessages([]);
    setClosed(false);
    setName("");
    setEmail("");
  };

  const send = async () => {
    const body = text.trim();
    if (!body || !sessionId) return;
    setText("");
    setMessages((m) => [...m, { sender: "visitor", text: body, created_at: new Date().toISOString() }]);
    try {
      await axios.post(`${API}/chat/${sessionId}/message`, { text: body });
    } catch (e) {
      if (e.response?.status === 410) setClosed(true);
    }
  };

  return (
    <>
      <button
        data-testid="chat-bubble-button"
        onClick={() => { setOpen(!open); setUnread(0); }}
        className="fixed bottom-4 right-4 z-40 w-11 h-11 rounded-full bg-[#39ff14] text-black flex items-center justify-center d1-glow hover:bg-white transition-colors"
        aria-label="Chat with us"
      >
        {open ? <X size={16} strokeWidth={2.5} /> : <MessageCircle size={16} strokeWidth={2.5} />}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#f5b700] text-black text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="chat-panel" className="fixed bottom-[4.25rem] right-4 z-40 w-[300px] max-w-[calc(100vw-2rem)] bg-[#121316] border border-[#22242a] flex flex-col shadow-2xl" style={{ height: "min(420px, 62vh)" }}>
          <div className="px-4 py-3 border-b border-[#22242a]">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#39ff14] animate-pulse"></span>
              <span className="f-disp text-sm uppercase text-white tracking-wide">The Print Plug</span>
            </div>
            <p className="f-mono text-[9px] text-[#8e929b] uppercase tracking-wider mt-1" data-testid="chat-response-time">
              Usually replies within a few hours — often much faster
            </p>
          </div>
          <div data-testid="chat-messages" className="flex-1 overflow-y-auto p-3 space-y-2">
            {!sessionId ? (
              <div className="mt-8 px-2 space-y-3" data-testid="chat-name-gate">
                <p className="f-body text-xs text-[#8e929b] text-center">
                  Your name + email, then you&apos;re connected. We email you if you leave before we reply.
                </p>
                <input
                  data-testid="chat-name-input"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setNameError(""); }}
                  placeholder="Your name"
                  className={`w-full bg-[#0a0a0c] border px-3 py-2.5 text-xs f-body text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14] ${nameError ? "border-red-500" : "border-[#22242a]"}`}
                />
                <input
                  data-testid="chat-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setNameError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && startChat()}
                  placeholder="Your email"
                  className={`w-full bg-[#0a0a0c] border px-3 py-2.5 text-xs f-body text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14] ${nameError ? "border-red-500" : "border-[#22242a]"}`}
                />
                {nameError && (
                  <p className="f-mono text-[10px] text-red-400 uppercase tracking-wider text-center">{nameError}</p>
                )}
                <button
                  data-testid="chat-start-button"
                  onClick={startChat}
                  className="w-full py-3 bg-[#39ff14] text-black f-mono text-xs uppercase tracking-[0.14em] font-bold hover:bg-white transition-colors"
                >
                  Start Chat
                </button>
              </div>
            ) : (
              <>
                {messages.length === 0 && (
                  <p className="f-body text-xs text-[#8e929b] text-center mt-8">
                    Questions about an order or product? Send a message — we reply right here.
                  </p>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.sender === "visitor" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-3 py-2 text-xs f-body leading-relaxed ${
                      m.sender === "visitor" ? "bg-[#39ff14] text-black" : "bg-[#22242a] text-[#ecebe4]"
                    }`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </>
            )}
          </div>
          {sessionId && closed && (
            <div className="p-3 border-t border-[#22242a] text-center space-y-2" data-testid="chat-ended">
              <p className="f-body text-xs text-[#8e929b]">This chat has ended.</p>
              <button
                data-testid="chat-start-new"
                onClick={startNew}
                className="w-full py-2.5 bg-[#39ff14] text-black f-mono text-xs uppercase tracking-[0.14em] font-bold hover:bg-white transition-colors"
              >
                Start New Chat
              </button>
            </div>
          )}
          {sessionId && !closed && (
            <div className="p-3 border-t border-[#22242a] flex gap-2">
              <input
                data-testid="chat-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Type a message…"
                className="flex-1 bg-[#0a0a0c] border border-[#22242a] px-3 py-2 text-xs f-body text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14]"
              />
              <button
                data-testid="chat-send-button"
                onClick={send}
                className="w-10 bg-[#39ff14] text-black flex items-center justify-center hover:bg-white transition-colors"
                aria-label="Send message"
              >
                <Send size={14} strokeWidth={2.5} />
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};
